/**
 * TechnicalLogBookEntry-part2.js
 * 
 * Sector Form Rendering Component
 * 
 * This file contains the renderSectorForm function which renders
 * the complete sector form with all fields including:
 * - Basic Sector Information
 * - Fuel Information
 * - Cruise Parameters
 * - Defect Report
 * - Inspections
 * - MEL Section
 * - BFF Certification
 * - Pilot Acceptance
 * - Engine Cleaning Details BFF
 */

import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox
} from '@mui/material';

import { Add, Delete, ExpandMore } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { convertForDisplay, convertToBaseUnit, formatUnitLabel } from '../../utils/unitConversion';
import { utcToDisplayDateAndTime, displayDateTimeToUTC, getTimezoneLabel } from '../../utils/timezoneUtils';
import { parseHMMToHours } from '../../utils/technicalLogBookCarriedForward';
import { getSectorCustomFieldSections, getSectorFields, MAX_TECHNICAL_LOG_BOOK_SECTOR_SLOTS } from '../../constants/technicalLogBookDefinition';
import { renderSectorFormPart3 } from './TechnicalLogBookEntry-part3';
import SignatureField from './SignatureField';
import NatureOfFlightSelect from './TechnicalLogBookEntryComponents/NatureOfFlightSelect';
import TlbCrewNameSelect from './TechnicalLogBookEntryComponents/TlbCrewNameSelect';
import {
  collectAmeNamesFromFormData,
  getAmeSignaturePresetsForField
} from '../../utils/technicalLogBookAmeSignature';
import { tlbFormatFieldProps, formatTechlogOilQuantityLabel } from '../../utils/technicalLogBookFieldFormat';

/**
 * Render sector form for a given sector number (Part 1: Basic/Fuel/Cruise)
 * @param {Object} params - Parameters object
 * @param {Number} params.sectorNumber - Sector number (1-5)
 * @param {Object} params.formData - Form data state
 * @param {Number} params.tabValue - Current tab value
 * @param {Object} params.natureOfFlightMapping - Nature of flight mapping
 * @param {Object} params.unitPreferences - Unit preferences for display
 * @param {Boolean} params.isReadOnly - Whether form is read-only
 * @param {Function} params.updateSectorField - Function to update sector field
 * @param {Function} params.updateSectorArrayField - Function to update sector array field
 * @param {Function} params.addInspection - Function to add inspection
 * @param {Function} params.removeInspection - Function to remove inspection
 * @param {String} params.entityId - Entity ID for signature/media records
 * @param {String} params.entityType - Entity type for signature/media records
 * @param {Object} params.sectorSection - Optional sector section from format definition (when present, simple fields rendered from it)
 * @param {Object[]} params.availableSectors - Trip sectors (with assignedPilots) for crew pickers
 * @param {Object[]} params.allCrewOptions - Full client crew list for "Other" picker
 * @returns {JSX.Element|null} - Sector form JSX or null
 */
const formatAssignedPilotName = (p) => {
  if (!p || typeof p !== 'object') return '';
  const fn = String(p.firstName || '').trim();
  const ln = String(p.lastName || '').trim();
  return [fn, ln].filter(Boolean).join(' ') || '';
};

const assignedPilotDisplayNames = (assignedPilots) => {
  if (!Array.isArray(assignedPilots)) return [];
  const names = assignedPilots.map(formatAssignedPilotName).filter(Boolean);
  return [...new Set(names)];
};

const pilotNamePresetsFromSector = (sec) => {
  const pic = String(sec?.picName || '').trim();
  const cop = String(sec?.coPilotName || '').trim();
  const out = [];
  if (pic) out.push(pic);
  if (cop && cop !== pic) out.push(cop);
  return out;
};

const getValueByPath = (obj, path) => {
  if (!obj || !path) return undefined;
  const keys = path.split('.');
  let v = obj;
  for (const k of keys) v = v?.[k];
  return v;
};

export const renderSectorForm = ({
  sectorNumber,
  formData,
  tabValue,
  natureOfFlightMapping,
  unitPreferences,
  isReadOnly,
  updateSectorField,
  updateAmeSignatureField,
  updateInspectionAmeSignatureField,
  updateSectorArrayField,
  addInspection,
  removeInspection,
  entityId = null,
  entityType = 'TECHNICAL_LOG_BOOK',
  sectorSection = null,
  displayTimezone,
  availableSectors = [],
  allCrewOptions = [],
  canManageSectors = false,
  maxSectorsPerTechlogBook = 3,
  onAddSector = null,
  onRemoveSector = null,
  onAddNatureOfFlightMapping = null
}) => {
  const sectorKey = `sector${sectorNumber}`;
  const sector = formData[sectorKey] || {};
  const sectorTabIndex = sectorNumber + 1; // Tab 2 = Sector 1, Tab 3 = Sector 2, etc.
  const slotsWithinMax = Array.from(
    { length: Math.max(0, maxSectorsPerTechlogBook - 1) },
    (_, i) => i + 2
  );
  const nextWithinMax = slotsWithinMax.find((n) => !formData?.[`sector${n}`]) || null;
  let highestSectorOnSheet = 0;
  for (let i = MAX_TECHNICAL_LOG_BOOK_SECTOR_SLOTS; i >= 1; i -= 1) {
    if (formData?.[`sector${i}`]) {
      highestSectorOnSheet = i;
      break;
    }
  }
  const overflowSectorNumber = (
    !nextWithinMax
    && highestSectorOnSheet >= maxSectorsPerTechlogBook
    && highestSectorOnSheet < MAX_TECHNICAL_LOG_BOOK_SECTOR_SLOTS
  ) ? highestSectorOnSheet + 1 : null;
  const nextSectorNumber = nextWithinMax || overflowSectorNumber;

  const linkedId = sector.linkedSectorId;
  const opsSector = linkedId
    ? (availableSectors || []).find((s) => String(s._id) === String(linkedId))
    : (availableSectors || []).find((s) => (s.seqNo || 0) === sectorNumber);
  const assignedPilotNames = assignedPilotDisplayNames(opsSector?.assignedPilots);
  const pilotNamePresets = pilotNamePresetsFromSector(sector);
  const picTrim = (sector.picName || '').trim();
  const previousAmeNames = collectAmeNamesFromFormData(formData);

  if (tabValue !== sectorTabIndex) return null;

  const tzLabel = displayTimezone ? getTimezoneLabel(displayTimezone) : '';
  const toDisplayDate = (v) => {
    if (!v || !displayTimezone) return null;
    const d = utcToDisplayDateAndTime(v, displayTimezone).date;
    return d ? dayjs(d) : null;
  };
  const toUTC = (dateStr) => {
    if (!dateStr || !displayTimezone) return null;
    return displayDateTimeToUTC(dateStr, '00:00', displayTimezone);
  };

  const normalizeHoursValue = (raw) => {
    if (!raw) return '';
    let value = String(raw).trim();
    if (!value) return '';
    if (!value.includes(':')) {
      value = value.slice(0, 6);
      if (!value) return '';
      return `${value}:00`;
    }
    const [hRaw, mRaw = ''] = value.split(':');
    const hours = hRaw.replace(/\D/g, '').slice(0, 6);
    const minutes = mRaw.replace(/\D/g, '').slice(0, 4);
    if (!hours) return '';
    return minutes ? `${hours}:${minutes}` : hours;
  };

  const getHoursHelperText = (raw) => {
    if (!raw) return 'H:MM — Stored As: 0.00';
    const value = String(raw).trim();
    if (!value) return 'H:MM — Stored As: 0.00';
    const [hPart = '', mPart = ''] = value.split(':');
    const h = parseInt(hPart, 10);
    const m = parseInt(mPart, 10) || 0;
    const decimal = parseHMMToHours(value);
    if (Number.isNaN(decimal)) return 'H:MM — Stored As: 0.00';
    const safeH = Number.isNaN(h) ? 0 : h;
    const safeM = Number.isNaN(m) ? 0 : m;
    return `h: ${safeH}, m: ${safeM} — Stored As: ${decimal.toFixed(2)}`;
  };

  const sectorCustomFieldGroups = getSectorCustomFieldSections(sectorSection);
  const hasVisibleSectorCustom = sectorCustomFieldGroups.some((g) =>
    (g.fields || []).some((f) => f.visible !== false)
  );
  const useDefinition =
    sectorSection &&
    sectorSection.visible !== false &&
    ((sectorSection.fields?.length > 0) || hasVisibleSectorCustom);
  // MEL fields are captured in the dedicated MEL Release block (part 3); exclude from Sector Details grid
  const sectorFields = useDefinition ? getSectorFields(sectorSection) : [];
  const simpleFields = useDefinition
    ? sectorFields.filter(
        (f) =>
          f.visible !== false &&
          f.type !== 'group' &&
          f.type !== 'array' &&
          f.path !== 'melReleased'
      )
    : [];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Sector {sectorNumber} Details
        </Typography>
        {canManageSectors && (
          <Box display="flex" gap={1} flexWrap="wrap" justifyContent="flex-end">
            {sectorNumber >= 2 && sectorNumber === highestSectorOnSheet && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<Delete />}
                onClick={() => onRemoveSector?.(sectorNumber)}
              >
                Delete Sector {sectorNumber}
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() => onAddSector?.(nextSectorNumber)}
              disabled={!nextSectorNumber}
            >
              {nextSectorNumber
                ? (overflowSectorNumber
                  ? `Add Sector ${overflowSectorNumber} (next sheet)`
                  : `Add Sector ${nextSectorNumber}`)
                : 'All Sectors Added'}
            </Button>
          </Box>
        )}
      </Box>

      {/* Definition-driven simple fields (when format has sector definition) */}
      {simpleFields.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2}>
            {simpleFields.map((field) => {
              const path = field.path;
              const value = getValueByPath(sector, path);
              const computedReadOnly = path === 'blockTime';
              const disabled = isReadOnly || (field.readOnly && !computedReadOnly);
              const label = formatTechlogOilQuantityLabel(field.label || path, path);
              if (field.readOnly && !computedReadOnly) {
                return (
                  <Grid item xs={12} md={6} key={path}>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                    <Typography variant="body2">{value != null && value !== '' ? String(value) : '—'}</Typography>
                  </Grid>
                );
              }
              switch (field.type) {
                case 'number':
                  return (
                    <Grid item xs={12} md={6} key={path}>
                      <TextField fullWidth label={label} type="number" value={value ?? ''} onChange={(e) => updateSectorField(sectorKey, path, e.target.value === '' ? undefined : Number(e.target.value))} disabled={disabled} required={!!field.required} {...tlbFormatFieldProps(value, path)} />
                    </Grid>
                  );
                case 'date':
                  return (
                    <Grid item xs={12} md={6} key={path}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker label={tzLabel ? `${label} (${tzLabel})` : label} value={displayTimezone ? toDisplayDate(value) : (value ? dayjs(value) : null)} onChange={(date) => updateSectorField(sectorKey, path, date ? (displayTimezone ? toUTC(date.format('YYYY-MM-DD')) : date.toDate()) : null)} disabled={disabled} slotProps={{ textField: { fullWidth: true } }} />
                      </LocalizationProvider>
                    </Grid>
                  );
                case 'dropdown':
                  if (path === 'natureOfFlight') {
                    return (
                      <Grid item xs={12} md={12} key={path}>
                        <NatureOfFlightSelect
                          label={label}
                          value={value ?? ''}
                          onChange={(code) => updateSectorField(sectorKey, path, code)}
                          disabled={disabled}
                          natureOfFlightMapping={natureOfFlightMapping}
                          onAddMapping={onAddNatureOfFlightMapping}
                        />
                      </Grid>
                    );
                  }
                  return (
                    <Grid item xs={12} md={12} key={path}>
                      <FormControl fullWidth disabled={disabled}>
                      <InputLabel sx={{ whiteSpace: 'nowrap' }}>{label}</InputLabel>
                        <Select value={value ?? ''} label={label} onChange={(e) => updateSectorField(sectorKey, path, e.target.value)}
                        sx={{
                           '& .MuiSelect-select': {
                             minWidth: '200px',
                             }
                             }}
                             >
                          {(field.options || []).map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label ?? opt.value}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  );
                case 'checkbox':
                  return (
                    <Grid item xs={12} md={6} key={path}>
                      <FormControlLabel control={<Checkbox checked={!!value} onChange={(e) => updateSectorField(sectorKey, path, e.target.checked)} disabled={disabled} />} label={label} />
                    </Grid>
                  );
                case 'multiline':
                  return (
                    <Grid item xs={12} key={path}>
                      <TextField fullWidth multiline minRows={2} label={label} value={value ?? ''} onChange={(e) => updateSectorField(sectorKey, path, e.target.value)} disabled={disabled} required={!!field.required} />
                    </Grid>
                  );
                case 'hours':
                  return (
                    <Grid item xs={12} md={6} key={path}>
                      <TextField
                        fullWidth
                        label={label}
                        value={value ?? ''}
                        onChange={(e) => updateSectorField(sectorKey, path, e.target.value)}
                        onBlur={(e) =>
                          updateSectorField(
                            sectorKey,
                            path,
                            normalizeHoursValue(e.target.value)
                          )
                        }
                        disabled={disabled}
                        required={!!field.required}
                        {...tlbFormatFieldProps(value, { fieldType: 'hours' }, getHoursHelperText(value))}
                      />
                    </Grid>
                  );
                default:
                  if (path === 'picName' || path === 'coPilotName') {
                    const coPilotAssignedNames = assignedPilotNames.filter((n) => n !== picTrim);
                    return (
                      <Grid item xs={12} md={6} key={path}>
                        <TlbCrewNameSelect
                          label={label}
                          value={value ?? ''}
                          onChange={(name) => updateSectorField(sectorKey, path, name)}
                          assignedPilots={path === 'coPilotName' ? coPilotAssignedNames : assignedPilotNames}
                          allCrew={allCrewOptions}
                          otherPicName={path === 'coPilotName' ? picTrim : null}
                          disabled={disabled}
                          allowEmpty={path === 'coPilotName'}
                        />
                      </Grid>
                    );
                  }
                  return (
                    <Grid item xs={12} md={6} key={path}>
                      <TextField
                        fullWidth
                        label={label}
                        value={value ?? ''}
                        onChange={computedReadOnly ? undefined : (e) => updateSectorField(sectorKey, path, e.target.value)}
                        disabled={disabled}
                        InputProps={computedReadOnly ? { readOnly: true } : undefined}
                        required={!!field.required}
                        {...tlbFormatFieldProps(
                          value,
                          path,
                          computedReadOnly ? 'Auto-calculated from rotor times (HHMM)' : undefined
                        )}
                      />
                    </Grid>
                  );
              }
            })}
          </Grid>
        </Paper>
      )}

      {/* Sector custom fields by group (from format definition SECTOR tab) */}
      {sectorCustomFieldGroups.map((group) => {
        const sectorCustomFields = (group.fields || []).filter((f) => f.visible !== false);
        if (sectorCustomFields.length === 0) return null;
        return (
          <Paper key={group.id} sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>{group.label}</Typography>
            <Grid container spacing={2}>
              {sectorCustomFields.map((field) => {
                const path = `customFields.${field.key}`;
                const value = sector.customFields?.[field.key];
                const disabled = isReadOnly || field.readOnly;
                const label = formatTechlogOilQuantityLabel(field.label || field.key, field.key);
                const fieldReactKey = `${group.id}-${field.key || 'field'}`;
                if (field.readOnly) {
                  return (
                    <Grid item xs={12} md={6} key={fieldReactKey}>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                      <Typography variant="body2">{value != null && value !== '' ? String(value) : '—'}</Typography>
                    </Grid>
                  );
                }
                switch (field.type) {
                  case 'number':
                    return (
                      <Grid item xs={12} md={6} key={fieldReactKey}>
                        <TextField fullWidth label={label} type="number" value={value ?? ''} onChange={(e) => updateSectorField(sectorKey, path, e.target.value === '' ? undefined : Number(e.target.value))} disabled={disabled} required={!!field.required} />
                      </Grid>
                    );
                  case 'date':
                    return (
                      <Grid item xs={12} md={6} key={fieldReactKey}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker label={tzLabel ? `${label} (${tzLabel})` : label} value={displayTimezone ? toDisplayDate(value) : (value ? dayjs(value) : null)} onChange={(date) => updateSectorField(sectorKey, path, date ? (displayTimezone ? toUTC(date.format('YYYY-MM-DD')) : date.toDate()) : null)} disabled={disabled} slotProps={{ textField: { fullWidth: true } }} />
                        </LocalizationProvider>
                      </Grid>
                    );
                  case 'time':
                    return (
                      <Grid item xs={12} md={6} key={fieldReactKey}>
                        <TextField fullWidth label={label} type="time" value={value ?? ''} onChange={(e) => updateSectorField(sectorKey, path, e.target.value)} disabled={disabled} required={!!field.required} InputLabelProps={{ shrink: true }} />
                      </Grid>
                    );
                  case 'dropdown':
                    return (
                      <Grid item xs={12} md={6} key={fieldReactKey}>
                        <FormControl fullWidth disabled={disabled}>
                          <InputLabel>{label}</InputLabel>
                          <Select value={value ?? ''} label={label} onChange={(e) => updateSectorField(sectorKey, path, e.target.value)}>
                            {(field.options || []).map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label ?? opt.value}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    );
                  case 'checkbox':
                    return (
                      <Grid item xs={12} md={6} key={fieldReactKey}>
                        <FormControlLabel control={<Checkbox checked={!!value} onChange={(e) => updateSectorField(sectorKey, path, e.target.checked)} disabled={disabled} />} label={label} />
                      </Grid>
                    );
                  case 'multiline':
                    return (
                      <Grid item xs={12} key={fieldReactKey}>
                        <TextField fullWidth multiline minRows={2} label={label} value={value ?? ''} onChange={(e) => updateSectorField(sectorKey, path, e.target.value)} disabled={disabled} required={!!field.required} />
                      </Grid>
                    );
                  case 'hours':
                    return (
                      <Grid item xs={12} md={6} key={fieldReactKey}>
                        <TextField
                          fullWidth
                          label={label}
                          value={value ?? ''}
                          onChange={(e) => updateSectorField(sectorKey, path, e.target.value)}
                          onBlur={(e) =>
                            updateSectorField(
                              sectorKey,
                              path,
                              normalizeHoursValue(e.target.value)
                            )
                          }
                          disabled={disabled}
                          required={!!field.required}
                          {...tlbFormatFieldProps(value, { fieldType: 'hours' }, getHoursHelperText(value))}
                        />
                      </Grid>
                    );
                  default:
                    return (
                      <Grid item xs={12} md={6} key={fieldReactKey}>
                        <TextField fullWidth label={label} value={value ?? ''} onChange={(e) => updateSectorField(sectorKey, path, e.target.value)} disabled={disabled} required={!!field.required} />
                      </Grid>
                    );
                }
              })}
            </Grid>
          </Paper>
        );
      })}

      {/* Basic Sector Information (when no definition or definition has no simple fields) */}
      {!useDefinition && (
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>Basic Information</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Sector Number"
              type="number"
              value={sector.sectorNumber || sectorNumber}
              onChange={(e) => updateSectorField(sectorKey, 'sectorNumber', parseInt(e.target.value) || sectorNumber)}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TlbCrewNameSelect
              label="PIC Name"
              value={sector.picName || ''}
              onChange={(name) => updateSectorField(sectorKey, 'picName', name)}
              assignedPilots={assignedPilotNames}
              allCrew={allCrewOptions}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TlbCrewNameSelect
              label="Co-Pilot Name"
              value={sector.coPilotName || ''}
              onChange={(name) => updateSectorField(sectorKey, 'coPilotName', name)}
              assignedPilots={assignedPilotNames.filter((n) => n !== picTrim)}
              allCrew={allCrewOptions}
              otherPicName={picTrim}
              disabled={isReadOnly}
              allowEmpty
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <NatureOfFlightSelect
              value={sector.natureOfFlight || ''}
              onChange={(code) => updateSectorField(sectorKey, 'natureOfFlight', code)}
              disabled={isReadOnly}
              natureOfFlightMapping={natureOfFlightMapping}
              onAddMapping={onAddNatureOfFlightMapping}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Departure Place"
              value={sector.departurePlace || ''}
              onChange={(e) => updateSectorField(sectorKey, 'departurePlace', e.target.value)}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label={tzLabel ? `Departure Date (${tzLabel})` : 'Departure Date'}
                value={displayTimezone ? toDisplayDate(sector.departureDate) : (sector.departureDate ? dayjs(sector.departureDate) : null)}
                onChange={(date) => updateSectorField(sectorKey, 'departureDate', date ? (displayTimezone ? toUTC(date.format('YYYY-MM-DD')) : date.toDate()) : null)}
                disabled={isReadOnly}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Arrival Place"
              value={sector.arrivalPlace || ''}
              onChange={(e) => updateSectorField(sectorKey, 'arrivalPlace', e.target.value)}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label={tzLabel ? `Arrival Date (${tzLabel})` : 'Arrival Date'}
                value={displayTimezone ? toDisplayDate(sector.arrivalDate) : (sector.arrivalDate ? dayjs(sector.arrivalDate) : null)}
                onChange={(date) => updateSectorField(sectorKey, 'arrivalDate', date ? (displayTimezone ? toUTC(date.format('YYYY-MM-DD')) : date.toDate()) : null)}
                disabled={isReadOnly}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={tzLabel ? `Rotor Start Time (${tzLabel})` : 'Rotor Start Time'}
              placeholder="HHMM"
              value={sector.rotorStartTimeUTC || ''}
              onChange={(e) => {
                const raw = (e.target.value || '').replace(/\D/g, '').slice(0, 4);
                updateSectorField(sectorKey, 'rotorStartTimeUTC', raw);
              }}
              disabled={isReadOnly}
              helperText="HHMM wall-clock in display timezone"
              {...tlbFormatFieldProps(sector.rotorStartTimeUTC, 'rotorStartTimeUTC')}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={tzLabel ? `Rotor Stop Time (${tzLabel})` : 'Rotor Stop Time'}
              placeholder="HHMM"
              value={sector.rotorStopTimeUTC || ''}
              onChange={(e) => {
                const raw = (e.target.value || '').replace(/\D/g, '').slice(0, 4);
                updateSectorField(sectorKey, 'rotorStopTimeUTC', raw);
              }}
              disabled={isReadOnly}
              helperText="HHMM wall-clock in display timezone"
              {...tlbFormatFieldProps(sector.rotorStopTimeUTC, 'rotorStopTimeUTC')}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Block Time"
              placeholder="HHMM"
              value={sector.blockTime || ''}
              InputProps={{ readOnly: true }}
              disabled={isReadOnly}
              {...tlbFormatFieldProps(sector.blockTime, 'blockTime', 'Auto-calculated from rotor times (HHMM)')}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Airbone Time"
              placeholder="H:MM"
              value={sector.airboneTime || ''}
              onChange={(e) => updateSectorField(sectorKey, 'airboneTime', e.target.value)}
              disabled={isReadOnly}
              {...tlbFormatFieldProps(sector.airboneTime, 'airboneTime')}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Landings"
              type="number"
              value={sector.landings || 0}
              onChange={(e) => updateSectorField(sectorKey, 'landings', parseInt(e.target.value) || 0)}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="IAP Count"
              type="number"
              value={sector.iapCount || 0}
              onChange={(e) => updateSectorField(sectorKey, 'iapCount', parseInt(e.target.value) || 0)}
              disabled={isReadOnly}
              helperText="Instrument Approach Procedure count"
            />
          </Grid>
          <Grid item xs={12}>
            <SignatureField
              label="Pilot Signature"
              value={sector.pilotSignature || ''}
              signatureType={sector.pilotSignatureType || 'NAME'}
              onChange={(value, type) => {
                updateSectorField(sectorKey, 'pilotSignature', value);
                updateSectorField(sectorKey, 'pilotSignatureType', type);
              }}
              disabled={isReadOnly}
              helperText="Signature of the pilot for this sector"
              entityId={entityId}
              entityType={entityType}
              namePresetOptions={pilotNamePresets}
            />
          </Grid>
        </Grid>
      </Paper>
      )}

      {/* Fuel Information */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Fuel and Oil Information
        </Typography>
        {(() => {
          const baseFuelInTankBF = Number(sector.fuelInTankBF ?? 0);
          const baseFuelUplifted = Number(sector.fuelUplifted ?? 0);
          const baseFuelAfterFlight = Number(sector.fuelAfterFlight ?? 0);
          const baseFuelConsumption = Number(sector.fuelConsumption ?? 0);
          const baseRhEngineOilConsumption = Number(sector.rhEngineOilConsumption ?? 0);
          const baseLhEngineOilConsumption = Number(sector.lhEngineOilConsumption ?? 0);
          const baseLhEngineOilInTankBF = Number(sector.lhEngineOilInTankBF ?? 0);
          const baseLhEngineOilUplifted = Number(sector.lhEngineOilUplifted ?? 0);
          const baseLhEngineOilBeforeFlight =
            (Number.isFinite(baseLhEngineOilInTankBF) ? baseLhEngineOilInTankBF : 0) +
            (Number.isFinite(baseLhEngineOilUplifted) ? baseLhEngineOilUplifted : 0);
          const baseLhEngineOilAfterFlight = Number(sector.lhEngineOilAfterFlight ?? 0);
          const baseRhEngineOilInTankBF = Number(sector.rhEngineOilInTankBF ?? 0);
          const baseRhEngineOilUplifted = Number(sector.rhEngineOilUplifted ?? 0);
          const baseRhEngineOilBeforeFlight =
            (Number.isFinite(baseRhEngineOilInTankBF) ? baseRhEngineOilInTankBF : 0) +
            (Number.isFinite(baseRhEngineOilUplifted) ? baseRhEngineOilUplifted : 0);
          const baseRhEngineOilAfterFlight = Number(sector.rhEngineOilAfterFlight ?? 0);
          const baseTotalFuelBeforeFlight =
            (Number.isFinite(baseFuelInTankBF) ? baseFuelInTankBF : 0) +
            (Number.isFinite(baseFuelUplifted) ? baseFuelUplifted : 0);

          return (
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={`Fuel in Tank BF (${formatUnitLabel('FUEL', unitPreferences.FUEL)})`}
              type="number"
              value={convertForDisplay(sector.fuelInTankBF, 'FUEL', unitPreferences.FUEL) || ''}
              onChange={(e) => {
                const baseValue = convertToBaseUnit(parseFloat(e.target.value) || 0, 'FUEL', unitPreferences.FUEL);
                updateSectorField(sectorKey, 'fuelInTankBF', baseValue);
              }}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={`Fuel Uplifted (${formatUnitLabel('FUEL', unitPreferences.FUEL)})`}
              type="number"
              value={convertForDisplay(sector.fuelUplifted, 'FUEL', unitPreferences.FUEL) || ''}
              onChange={(e) => {
                const baseValue = convertToBaseUnit(parseFloat(e.target.value) || 0, 'FUEL', unitPreferences.FUEL);
                updateSectorField(sectorKey, 'fuelUplifted', baseValue);
              }}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={`Total Fuel Before Flight (${formatUnitLabel('FUEL', unitPreferences.FUEL)})`}
              type="number"
              value={convertForDisplay(baseTotalFuelBeforeFlight, 'FUEL', unitPreferences.FUEL) || ''}
              InputProps={{ readOnly: true }}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={`Fuel After Flight (${formatUnitLabel('FUEL', unitPreferences.FUEL)})`}
              type="number"
              value={convertForDisplay(baseFuelAfterFlight, 'FUEL', unitPreferences.FUEL) || ''}
              onChange={(e) => {
                const baseValue = convertToBaseUnit(parseFloat(e.target.value) || 0, 'FUEL', unitPreferences.FUEL);
                updateSectorField(sectorKey, 'fuelAfterFlight', baseValue);
              }}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={`Fuel Consumption (${formatUnitLabel('FUEL', unitPreferences.FUEL)})`}
              type="number"
              value={convertForDisplay(baseFuelConsumption, 'FUEL', unitPreferences.FUEL) || ''}
              onChange={(e) => {
                const baseValue = convertToBaseUnit(parseFloat(e.target.value) || 0, 'FUEL', unitPreferences.FUEL);
                updateSectorField(sectorKey, 'fuelConsumption', baseValue);
              }}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 1 }}>
              Oil Information (ml)
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Oil Consumption in RH Engine (ml)"
              type="number"
              value={baseRhEngineOilConsumption || ''}
              InputProps={{ readOnly: true }}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Oil Consumption in LH Engine (ml)"
              type="number"
              value={baseLhEngineOilConsumption || ''}
              InputProps={{ readOnly: true }}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="LH Engine Oil in Tank BF (ml)"
              type="number"
              value={baseLhEngineOilInTankBF || ''}
              onChange={(e) => updateSectorField(sectorKey, 'lhEngineOilInTankBF', parseFloat(e.target.value) || 0)}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="LH Engine Oil Uplifted (ml)"
              type="number"
              value={baseLhEngineOilUplifted || ''}
              onChange={(e) => updateSectorField(sectorKey, 'lhEngineOilUplifted', parseFloat(e.target.value) || 0)}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="LH Engine Oil Before Flight (ml)"
              type="number"
              value={baseLhEngineOilBeforeFlight || ''}
              InputProps={{ readOnly: true }}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="LH Engine Oil After Flight (ml)"
              type="number"
              value={baseLhEngineOilAfterFlight || ''}
              onChange={(e) => updateSectorField(sectorKey, 'lhEngineOilAfterFlight', parseFloat(e.target.value) || 0)}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="RH Engine Oil in Tank BF (ml)"
              type="number"
              value={baseRhEngineOilInTankBF || ''}
              onChange={(e) => updateSectorField(sectorKey, 'rhEngineOilInTankBF', parseFloat(e.target.value) || 0)}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="RH Engine Oil Uplifted (ml)"
              type="number"
              value={baseRhEngineOilUplifted || ''}
              onChange={(e) => updateSectorField(sectorKey, 'rhEngineOilUplifted', parseFloat(e.target.value) || 0)}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="RH Engine Oil Before Flight (ml)"
              type="number"
              value={baseRhEngineOilBeforeFlight || ''}
              InputProps={{ readOnly: true }}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="RH Engine Oil After Flight (ml)"
              type="number"
              value={baseRhEngineOilAfterFlight || ''}
              onChange={(e) => updateSectorField(sectorKey, 'rhEngineOilAfterFlight', parseFloat(e.target.value) || 0)}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12}>
            <SignatureField
              label="AME Authorized Person / Pilot Signature"
              value={sector.ameAuthPersonPilotSignature || ''}
              signatureType={sector.ameAuthPersonPilotSignatureType || 'NAME'}
              onChange={(value, type) => {
                updateAmeSignatureField(
                  sectorKey,
                  'ameAuthPersonPilotSignature',
                  'ameAuthPersonPilotSignatureType',
                  value,
                  type
                );
              }}
              disabled={isReadOnly}
              helperText="Signature of AME authorized person or pilot for fuel information"
              entityId={entityId}
              entityType={entityType}
              namePresetOptions={pilotNamePresets}
              ameNameOptions={previousAmeNames.filter(
                (n) => n !== String(sector.ameAuthPersonPilotSignature || '').trim()
              )}
              ameSignaturePresets={getAmeSignaturePresetsForField(sector, 'ameAuthPersonPilotSignature')}
            />
          </Grid>
        </Grid>
          );
        })()}
      </Paper>

      {/* Cruise Parameters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>Cruise Parameters</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={`Altitude Zp (${formatUnitLabel('ALTITUDE', unitPreferences.ALTITUDE)})`}
              type="number"
              value={convertForDisplay(sector.cruiseParameters?.altitudeZp, 'ALTITUDE', unitPreferences.ALTITUDE) || ''}
              onChange={(e) => {
                const baseValue = convertToBaseUnit(parseFloat(e.target.value) || 0, 'ALTITUDE', unitPreferences.ALTITUDE);
                updateSectorField(sectorKey, 'cruiseParameters.altitudeZp', baseValue);
              }}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={`OAT (${formatUnitLabel('TEMPERATURE', unitPreferences.TEMPERATURE)})`}
              type="number"
              value={convertForDisplay(sector.cruiseParameters?.oat, 'TEMPERATURE', unitPreferences.TEMPERATURE) || ''}
              onChange={(e) => {
                const baseValue = convertToBaseUnit(parseFloat(e.target.value) || 0, 'TEMPERATURE', unitPreferences.TEMPERATURE);
                updateSectorField(sectorKey, 'cruiseParameters.oat', baseValue);
              }}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={`IAS (${formatUnitLabel('SPEED', unitPreferences.SPEED)})`}
              type="number"
              value={convertForDisplay(sector.cruiseParameters?.ias, 'SPEED', unitPreferences.SPEED) || ''}
              onChange={(e) => {
                const baseValue = convertToBaseUnit(parseFloat(e.target.value) || 0, 'SPEED', unitPreferences.SPEED);
                updateSectorField(sectorKey, 'cruiseParameters.ias', baseValue);
              }}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="NR (%)"
              type="number"
              value={sector.cruiseParameters?.nr || ''}
              onChange={(e) => updateSectorField(sectorKey, 'cruiseParameters.nr', parseFloat(e.target.value) || 0)}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="LH Engine TRQ (%)"
              type="number"
              value={sector.cruiseParameters?.lhEngineTRQ || ''}
              onChange={(e) => updateSectorField(sectorKey, 'cruiseParameters.lhEngineTRQ', parseFloat(e.target.value) || 0)}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="LH Engine N1/Ng (%)"
              type="number"
              value={sector.cruiseParameters?.lhEngineN1Ng || ''}
              onChange={(e) => updateSectorField(sectorKey, 'cruiseParameters.lhEngineN1Ng', parseFloat(e.target.value) || 0)}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label={`LH Engine ITT/TOT (${formatUnitLabel('TEMPERATURE', unitPreferences.TEMPERATURE)})`}
              type="number"
              value={convertForDisplay(sector.cruiseParameters?.lhEngineITTTOT, 'TEMPERATURE', unitPreferences.TEMPERATURE) || ''}
              onChange={(e) => {
                const baseValue = convertToBaseUnit(parseFloat(e.target.value) || 0, 'TEMPERATURE', unitPreferences.TEMPERATURE);
                updateSectorField(sectorKey, 'cruiseParameters.lhEngineITTTOT', baseValue);
              }}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label={`LH Engine Oil Temp (${formatUnitLabel('TEMPERATURE', unitPreferences.TEMPERATURE)})`}
              type="number"
              value={convertForDisplay(sector.cruiseParameters?.lhEngineOilTemperature, 'TEMPERATURE', unitPreferences.TEMPERATURE) || ''}
              onChange={(e) => {
                const baseValue = convertToBaseUnit(parseFloat(e.target.value) || 0, 'TEMPERATURE', unitPreferences.TEMPERATURE);
                updateSectorField(sectorKey, 'cruiseParameters.lhEngineOilTemperature', baseValue);
              }}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label={`LH Engine Oil Pressure (${formatUnitLabel('PRESSURE', unitPreferences.PRESSURE)})`}
              type="number"
              value={convertForDisplay(sector.cruiseParameters?.lhEngineOilPressure, 'PRESSURE', unitPreferences.PRESSURE) || ''}
              onChange={(e) => {
                const baseValue = convertToBaseUnit(parseFloat(e.target.value) || 0, 'PRESSURE', unitPreferences.PRESSURE);
                updateSectorField(sectorKey, 'cruiseParameters.lhEngineOilPressure', baseValue);
              }}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="RH Engine TRQ (%)"
              type="number"
              value={sector.cruiseParameters?.rhEngineTRQ || ''}
              onChange={(e) => updateSectorField(sectorKey, 'cruiseParameters.rhEngineTRQ', parseFloat(e.target.value) || 0)}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="RH Engine N1/Ng (%)"
              type="number"
              value={sector.cruiseParameters?.rhEngineN1Ng || ''}
              onChange={(e) => updateSectorField(sectorKey, 'cruiseParameters.rhEngineN1Ng', parseFloat(e.target.value) || 0)}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label={`RH Engine ITT/TOT (${formatUnitLabel('TEMPERATURE', unitPreferences.TEMPERATURE)})`}
              type="number"
              value={convertForDisplay(sector.cruiseParameters?.rhEngineITTTOT, 'TEMPERATURE', unitPreferences.TEMPERATURE) || ''}
              onChange={(e) => {
                const baseValue = convertToBaseUnit(parseFloat(e.target.value) || 0, 'TEMPERATURE', unitPreferences.TEMPERATURE);
                updateSectorField(sectorKey, 'cruiseParameters.rhEngineITTTOT', baseValue);
              }}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label={`RH Engine Oil Temp (${formatUnitLabel('TEMPERATURE', unitPreferences.TEMPERATURE)})`}
              type="number"
              value={convertForDisplay(sector.cruiseParameters?.rhEngineOilTemperature, 'TEMPERATURE', unitPreferences.TEMPERATURE) || ''}
              onChange={(e) => {
                const baseValue = convertToBaseUnit(parseFloat(e.target.value) || 0, 'TEMPERATURE', unitPreferences.TEMPERATURE);
                updateSectorField(sectorKey, 'cruiseParameters.rhEngineOilTemperature', baseValue);
              }}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label={`RH Engine Oil Pressure (${formatUnitLabel('PRESSURE', unitPreferences.PRESSURE)})`}
              type="number"
              value={convertForDisplay(sector.cruiseParameters?.rhEngineOilPressure, 'PRESSURE', unitPreferences.PRESSURE) || ''}
              onChange={(e) => {
                const baseValue = convertToBaseUnit(parseFloat(e.target.value) || 0, 'PRESSURE', unitPreferences.PRESSURE);
                updateSectorField(sectorKey, 'cruiseParameters.rhEngineOilPressure', baseValue);
              }}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label={`MGB Oil Temperature (${formatUnitLabel('TEMPERATURE', unitPreferences.TEMPERATURE)})`}
              type="number"
              value={convertForDisplay(sector.cruiseParameters?.mgbOilTemperature, 'TEMPERATURE', unitPreferences.TEMPERATURE) || ''}
              onChange={(e) => {
                const baseValue = convertToBaseUnit(parseFloat(e.target.value) || 0, 'TEMPERATURE', unitPreferences.TEMPERATURE);
                updateSectorField(sectorKey, 'cruiseParameters.mgbOilTemperature', baseValue);
              }}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label={`MGB Oil Pressure (${formatUnitLabel('PRESSURE', unitPreferences.PRESSURE)})`}
              type="number"
              value={convertForDisplay(sector.cruiseParameters?.mgbOilPressure, 'PRESSURE', unitPreferences.PRESSURE) || ''}
              onChange={(e) => {
                const baseValue = convertToBaseUnit(parseFloat(e.target.value) || 0, 'PRESSURE', unitPreferences.PRESSURE);
                updateSectorField(sectorKey, 'cruiseParameters.mgbOilPressure', baseValue);
              }}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label={`TGB Oil Temp (${formatUnitLabel('TEMPERATURE', unitPreferences.TEMPERATURE)})`}
              type="number"
              value={convertForDisplay(sector.cruiseParameters?.tgbOilTemp, 'TEMPERATURE', unitPreferences.TEMPERATURE) || ''}
              onChange={(e) => {
                const baseValue = convertToBaseUnit(parseFloat(e.target.value) || 0, 'TEMPERATURE', unitPreferences.TEMPERATURE);
                updateSectorField(sectorKey, 'cruiseParameters.tgbOilTemp', baseValue);
              }}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Exedence if any"
              value={sector.cruiseParameters?.exedenceIfAny || ''}
              onChange={(e) => updateSectorField(sectorKey, 'cruiseParameters.exedenceIfAny', e.target.value)}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Misc"
              value={sector.cruiseParameters?.misc || ''}
              onChange={(e) => updateSectorField(sectorKey, 'cruiseParameters.misc', e.target.value)}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12}>
            <SignatureField
              label="Pilot Signature (Cruise Parameters)"
              value={sector.cruiseParameters?.pilotSignature || ''}
              signatureType={sector.cruiseParameters?.pilotSignatureType || 'NAME'}
              onChange={(value, type) => {
                updateSectorField(sectorKey, 'cruiseParameters.pilotSignature', value);
                updateSectorField(sectorKey, 'cruiseParameters.pilotSignatureType', type);
              }}
              disabled={isReadOnly}
              helperText="Pilot signature for cruise parameters"
              entityId={entityId}
              entityType={entityType}
              namePresetOptions={pilotNamePresets}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Render Part 3: Defect Report, Inspections, MEL, BFF, Pilot Acceptance, Engine Cleaning */}
      {renderSectorFormPart3({
        sectorKey,
        sector,
        isReadOnly,
        updateSectorField,
        updateAmeSignatureField,
        updateInspectionAmeSignatureField,
        updateSectorArrayField,
        addInspection,
        removeInspection,
        entityId,
        entityType,
        pilotNamePresets,
        previousAmeNames
      })}
    </Box>
  );
};
